import type { Journey as JourneyData } from '@commissionsight/sdk';
import { formatPeriodLong } from '@commissionsight/shared';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../api.js';
import { Card, FlagChips, Loading, StatPill } from '../components/ui.js';
import { formatCurrency } from '../lib/status.js';

const fmtDate = (ms: number | null) =>
  ms
    ? new Date(ms).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : '—';

/**
 * Full audit "journey" of a member or a single policy — every period it
 * appeared, the source file, commission/premium, status + flags, and the
 * field-level changes. Used by /members/:memberRefId and /policies/:policyRefId.
 */
interface LinkedDispute {
  id: string;
  title: string;
  amountCents: number;
  currency: string;
  status: string;
}

export function Journey() {
  const { client, baseUrl, token, entitlements } = useAuth();
  const navigate = useNavigate();
  const { memberRefId, policyRefId } = useParams();
  // Go back to wherever the user came from (preserving that page's filters/scroll),
  // falling back to the jobs list if there's no in-app history (e.g. a deep link).
  const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate('/jobs'));
  const [data, setData] = useState<JourneyData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [disputes, setDisputes] = useState<LinkedDispute[]>([]);

  useEffect(() => {
    setData(null);
    setError(null);
    const p = policyRefId
      ? client.getPolicyJourney(policyRefId)
      : memberRefId
        ? client.getMemberJourney(memberRefId)
        : Promise.reject(new Error('No id'));
    p.then(setData).catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, [client, memberRefId, policyRefId]);

  // Disputes linked to this member/policy (enterprise, app-only — kept off the SDK
  // journey so dispute data never crosses the public API boundary).
  useEffect(() => {
    setDisputes([]);
    if (!entitlements.disputes) return;
    const q = policyRefId
      ? `policyRefId=${policyRefId}`
      : memberRefId
        ? `memberRefId=${memberRefId}`
        : '';
    if (!q) return;
    fetch(`${baseUrl.replace(/\/$/, '')}/disputes?${q}`, {
      headers: { authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((b) => setDisputes(b.data ?? []))
      .catch(() => {});
  }, [baseUrl, token, entitlements.disputes, memberRefId, policyRefId]);

  if (error) return <p className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>;
  if (!data) return <Loading label="Loading journey…" />;

  // Newest first — investigators usually start from the most recent period.
  const periods = [...data.periods].reverse();
  const refId = policyRefId ?? data.memberRefId;

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2 text-sm text-neutral-500">
          <button type="button" onClick={goBack} className="hover:text-emerald-600">
            ← Back
          </button>
        </div>
        <h1 className="mt-1 text-2xl font-semibold">
          {data.member.memberName ?? data.member.memberExternalId ?? 'Member'}
        </h1>
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-neutral-500">
          {data.member.memberExternalId && <span>Member ID: {data.member.memberExternalId}</span>}
          {data.member.email && <span>{data.member.email}</span>}
          <span className="font-mono text-xs">
            {policyRefId ? 'policy' : 'member'} {refId.slice(0, 16)}…
          </span>
        </div>
        <p className="mt-2 text-sm text-neutral-500">
          {data.periodCount} period{data.periodCount === 1 ? '' : 's'} ·{' '}
          {data.firstPeriod && `first seen ${data.firstPeriod}`}
          {data.latestPeriod && ` · latest ${data.latestPeriod}`}
          {!policyRefId && (
            <>
              {' · '}
              <Link
                className="text-emerald-600 hover:underline"
                to={`/members/${data.memberRefId}`}
              >
                member view
              </Link>
            </>
          )}
        </p>
      </div>

      {disputes.length > 0 && (
        <Card>
          <h2 className="mb-2 text-sm font-semibold">
            Linked disputes <span className="text-neutral-400">({disputes.length})</span>
          </h2>
          <ul className="space-y-1.5">
            {disputes.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-3 text-sm">
                <Link
                  to="/disputes"
                  className="truncate text-emerald-700 hover:underline dark:text-emerald-400"
                >
                  {d.title}
                </Link>
                <span className="flex shrink-0 items-center gap-3">
                  <span
                    className={`tabular-nums ${d.amountCents < 0 ? 'text-rose-600 dark:text-rose-400' : ''}`}
                  >
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: d.currency || 'USD',
                      currencySign: 'accounting',
                    }).format(d.amountCents / 100)}
                  </span>
                  <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
                    {d.status.replace('_', ' ')}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <ol className="space-y-3">
        {periods.map((p) => {
          // A clawback: the carrier reversed commission this period. It's an
          // adverse event, so the row reads as a red alert even though the
          // member's presence-status (green/yellow) is unchanged.
          const isChargeback = p.flags.includes('CHARGEBACK') || (p.commissionAmount ?? 0) < 0;
          return (
            <li key={p.period}>
              <Card
                className={
                  isChargeback ? 'border-l-4 border-l-rose-500 dark:border-l-rose-500' : ''
                }
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="font-semibold">
                      {formatPeriodLong({ year: p.periodYear, month: p.periodMonth })}
                    </span>
                    {p.status && <StatPill status={p.status} />}
                    {p.firstSeen && (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                        First appeared
                      </span>
                    )}
                    <FlagChips flags={p.flags} />
                  </div>
                  <div className="text-right">
                    <div
                      className={`font-semibold tabular-nums ${
                        p.present && (p.commissionAmount ?? 0) < 0
                          ? 'text-rose-600 dark:text-rose-400'
                          : ''
                      }`}
                    >
                      {p.present ? formatCurrency(p.commissionAmount) : '—'}
                    </div>
                    {!p.present && p.prevCommissionAmount != null ? (
                      <div className="text-xs text-neutral-400">
                        was {formatCurrency(p.prevCommissionAmount)}
                      </div>
                    ) : (
                      p.premiumAmount != null && (
                        <div className="text-xs text-neutral-400">
                          premium {formatCurrency(p.premiumAmount)}
                        </div>
                      )
                    )}
                  </div>
                </div>

                <div className="mt-2 text-xs text-neutral-500">
                  {p.present ? (
                    p.file ? (
                      <>
                        From{' '}
                        <span className="font-medium text-neutral-700">
                          {p.file.fileName ?? p.file.fileId}
                        </span>
                        {' · uploaded '}
                        {fmtDate(p.file.uploadedAt)}
                      </>
                    ) : (
                      'Source file purged'
                    )
                  ) : p.file ? (
                    <>
                      Absent from{' '}
                      <span className="font-medium text-neutral-700">
                        {p.file.fileName ?? p.file.fileId}
                      </span>{' '}
                      — dropped off the book
                    </>
                  ) : (
                    'Absent from this period’s statement — dropped off the book'
                  )}
                </div>

                {/* Per-policy breakdown for the member view — shown for every
                    present period (even a single policy) so each record exposes
                    the same drill-in link and policy/plan detail as the policy view. */}
                {!policyRefId && p.policies.length >= 1 && (
                  <div className="mt-3 divide-y divide-neutral-100 border-t border-neutral-100 pt-2 text-sm dark:divide-neutral-800 dark:border-neutral-800">
                    {p.policies.map((pol) => (
                      <div
                        key={pol.policyRefId}
                        className="flex items-center justify-between py-1.5"
                      >
                        <Link
                          to={`/policies/${pol.policyRefId}`}
                          className="text-emerald-600 hover:underline"
                        >
                          {pol.policyNumber ?? pol.policyRefId.slice(0, 12)}
                          {pol.planName ? ` · ${pol.planName}` : ''}
                        </Link>
                        <span
                          className={`tabular-nums ${
                            pol.commissionAmount < 0 ? 'text-rose-600 dark:text-rose-400' : ''
                          }`}
                        >
                          {formatCurrency(pol.commissionAmount)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Field-level changes detected entering this period. */}
                {p.deltas.length > 0 && (
                  <div className="mt-3 border-t border-neutral-100 pt-2 dark:border-neutral-800">
                    <div className="text-xs font-medium uppercase tracking-wide text-neutral-400">
                      Changes
                    </div>
                    <ul className="mt-1 space-y-0.5 text-sm">
                      {p.deltas.map((d) => (
                        <li key={d.field} className="text-neutral-600 dark:text-neutral-300">
                          <span className="font-medium">{d.field}</span>:{' '}
                          <span className="text-neutral-400 line-through">
                            {d.prevValue ?? '∅'}
                          </span>
                          {' → '}
                          <span>{d.currValue ?? '∅'}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Card>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
